import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type MeasureBundleObject, useShouldPromptForCSSRS } from '@grow-therapy-team/seedling-components';

import { showChildCSSRSGate, showKaiserIntakeCSSRSGate } from '../../statsig/gates';
import { toast } from '../../toasts';
import { roomAtom } from '../../video-sdk';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { ProviderDrawerState } from '../state';
import { CSSRSPromptToast } from './CSSRSPromptToast';
import { getCSSRSPromptToastCopy } from './utils';

const TOAST_KEY = 'cssrs-prompt';

type PromptPayload = {
  visitorName: string;
  visitorUuid: string;
  patientShortId: string;
  appointmentShortId?: string;
  patientChart: {
    preferredShortName?: string | null;
    measureBundles?: MeasureBundleObject[];
  };
};

export function useCSSRSPromptToastCallback() {
  const room = useAtomValue(roomAtom);
  const openClientInformation = useOpenClientInformationCallback();
  const [promptPayload, setPromptPayload] = useState<PromptPayload | null>(null);

  const { loading, result } = useShouldPromptForCSSRS({
    patientShortId: promptPayload?.patientShortId ?? '',
    measureBundles: promptPayload?.patientChart?.measureBundles ?? [],
    appointmentShortId: promptPayload?.appointmentShortId,
  });

  const { value: kaiserGate } = useFeatureGate(showKaiserIntakeCSSRSGate);
  const { value: childGate } = useFeatureGate(showChildCSSRSGate);

  const patientDisplayName = promptPayload
    ? promptPayload.patientChart.preferredShortName || promptPayload.visitorName
    : '';

  const toastCopy = useMemo(
    () =>
      !promptPayload || loading
        ? undefined
        : getCSSRSPromptToastCopy(result, patientDisplayName, kaiserGate, childGate),
    [promptPayload, loading, result, patientDisplayName, kaiserGate, childGate],
  );

  useEffect(
    function clearToastOnRoomDisconnect() {
      if (!room) {
        toast.close(TOAST_KEY);
      }
    },
    [room],
  );

  useEffect(
    function enqueueAfterPromptResolved() {
      if (!promptPayload || loading) {
        return;
      }
      if (!toastCopy) {
        setPromptPayload(null);
        return;
      }

      const payload = promptPayload;
      setPromptPayload(null);

      toast.enqueue(
        ({ closeToast, ...ariaProps }) => (
          <CSSRSPromptToast
            {...ariaProps}
            heading={toastCopy.heading}
            body={toastCopy.body}
            variant={toastCopy.variant}
            callToAction={toastCopy.callToAction}
            onAdministerCSSRS={() => {
              openClientInformation(
                { visitorUuid: payload.visitorUuid, patientShortId: payload.patientShortId },
                ProviderDrawerState.CSSRS_FORM,
              );
              closeToast();
            }}
          />
        ),
        { key: TOAST_KEY, timeout: Infinity },
      );
    },
    [promptPayload, loading, toastCopy, openClientInformation],
  );

  return useCallback(
    (
      {
        visitorName,
        visitorUuid,
        patientShortId,
        appointmentShortId,
      }: {
        visitorName: string;
        visitorUuid: string;
        patientShortId: string;
        appointmentShortId?: string;
      },
      patientChart?: {
        preferredShortName?: string | null;
        measureBundles?: MeasureBundleObject[];
      } | null,
    ) => {
      if (!patientChart) {
        return;
      }

      if (!patientChart.measureBundles?.length) {
        return;
      }

      setPromptPayload({ visitorName, visitorUuid, patientShortId, appointmentShortId, patientChart });
    },
    [],
  );
}
